import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { FuturesPaperPositionStatus } from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';

export default function FuturesPaperOpeningRecovery({
    position,
    onApplied
}: {
    position: FuturesPaperPositionStatus;
    onApplied: () => Promise<void>;
}) {
    const [mode, setMode] = useState<'order' | 'none'>('order');
    const [reference, setReference] = useState('');
    const [reason, setReason] = useState('');
    const [review, setReview] = useState<{ mode: 'order' | 'none'; reference: string; reason: string } | null>(null);
    const [acknowledged, setAcknowledged] = useState(false);
    const [message, setMessage] = useState('');
    const order = useMutation(trpc.admin.reconcileFuturesPaperOpening.mutationOptions({ retry: false }));
    const none = useMutation(trpc.admin.confirmNoFuturesPaperOpening.mutationOptions({ retry: false }));
    const busy = order.isPending || none.isPending;
    const reset = () => {
        setReview(null);
        setAcknowledged(false);
        setMessage('');
    };
    const inspect = async (apply: boolean) => {
        const input = apply ? review : { mode, reference, reason };
        if (!input) return;
        try {
            const shared = { positionId: position.positionId, clientOrderId: position.clientOrderId, reason: input.reason, apply };
            if (input.mode === 'order') {
                const result = await order.mutateAsync({
                    ...shared,
                    providerOrderId: input.reference,
                    acknowledgeOriginalBrokerOrder: true
                });
                setMessage(
                    `Original broker order: ${result.brokerStatus}. ${apply ? 'Reconciled.' : 'Identity checks passed; review before applying.'}`
                );
            } else {
                await none.mutateAsync({ ...shared, brokerReference: input.reference, acknowledgeBrokerConfirmedNoOrder: true });
                setMessage(
                    apply
                        ? 'Broker-confirmed no-order decision saved; normal flat reconciliation still controls release.'
                        : 'Review recorded details. An empty lookup or timeout is not proof that no order exists.'
                );
            }
            if (apply) {
                setReview(null);
                setAcknowledged(false);
                await onApplied();
            } else setReview(input);
        } catch (cause) {
            setMessage(cause instanceof Error ? cause.message : String(cause));
            setReview(null);
        }
    };
    return (
        <section className="space-y-3 rounded border border-amber-700 p-4">
            <h3 className="font-semibold">Reconcile the original opening</h3>
            <p className="text-sm">
                Submission reference: <code>{position.clientOrderId}</code>. Inspect this exact submission in the broker records. This
                action never sends another opening order.
            </p>
            <label className="block">
                Broker evidence
                <select
                    className="ml-3 rounded bg-slate-900 p-2"
                    value={mode}
                    onChange={(e) => {
                        setMode(e.target.value === 'none' ? 'none' : 'order');
                        reset();
                    }}
                >
                    <option value="order">Original broker order found</option>
                    <option value="none">Broker confirmed no order exists</option>
                </select>
            </label>
            <label className="block">
                {mode === 'order' ? 'Original broker order ID' : 'Broker confirmation reference'}
                <input
                    className="ml-3 rounded bg-slate-900 p-2"
                    value={reference}
                    onChange={(e) => {
                        setReference(e.target.value);
                        reset();
                    }}
                />
            </label>
            <label className="block">
                Review notes
                <textarea
                    className="mt-1 block w-full rounded bg-slate-900 p-2"
                    minLength={20}
                    value={reason}
                    onChange={(e) => {
                        setReason(e.target.value);
                        reset();
                    }}
                />
            </label>
            <button
                className="rounded bg-slate-700 px-3 py-2 disabled:opacity-40"
                disabled={busy || !reference || reason.trim().length < 20}
                onClick={() => void inspect(false)}
            >
                Preview recovery
            </button>
            {message && <p role="status">{message}</p>}
            {review && (
                <div className="space-y-3">
                    <label className="block">
                        <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} /> I checked the
                        original broker record and confirm this evidence belongs to this submission.
                    </label>
                    <button
                        className="rounded bg-amber-700 px-3 py-2 disabled:opacity-40"
                        disabled={busy || !acknowledged}
                        onClick={() => void inspect(true)}
                    >
                        Apply reviewed recovery
                    </button>
                </div>
            )}
        </section>
    );
}
